// Static prompts shown one-by-one while the sender records a wishing/
// congratulating video for the recipient.
//
// `question` is the longer prompt the sender reads on screen.
// `subject` is the short topic label burned into the recorded video so
// the recipient understands what each segment is about.
//
// Keys must match the strings in the `occasions` array in index.jsx exactly.

export const occasionQuestions = {
  Wedding: [
    { question: "Who is this wedding message for, and what's your relationship to them?", subject: 'Who they are' },
    { question: 'What was your reaction when you heard they were getting married?', subject: 'Reaction to the news' },
    { question: 'Share a favorite memory you have with the couple.', subject: 'A favorite memory together' },
    { question: 'What do you admire most about them as a couple?', subject: 'What you admire about them' },
    { question: 'Leave them with a heartfelt wish or blessing for their life together.', subject: 'A wish for their life together' },
  ],

  'New Baby': [
    { question: "Who is the new baby, and what's your relationship to the family?", subject: 'Who they are' },
    { question: 'How did you feel when you heard the baby was on the way?', subject: 'Reaction to the news' },
    { question: 'Share a hope or dream you have for this little one.', subject: 'A hope for the baby' },
    { question: 'What advice would you give the new parents?', subject: 'Advice for the parents' },
    { question: "What's the one thing you most want this baby to know growing up?", subject: 'A message for the baby' },
  ],

  Anniversary: [
    { question: 'Whose anniversary are you celebrating, and how do you know them?', subject: 'Who they are' },
    { question: "What's a favorite memory you have of them as a couple?", subject: 'A favorite memory of them' },
    { question: 'What do you admire most about their relationship?', subject: 'What you admire about them' },
    { question: "What's a lesson their love story has taught you?", subject: 'What their love taught you' },
    { question: 'Share a heartfelt wish for the years still ahead of them.', subject: 'A wish for the years ahead' },
  ],

  'Celebration of Life': [
    { question: 'Who are you honoring today, and what was your relationship to them?', subject: "Who you're honoring" },
    { question: 'Share a memory of them that always makes you smile.', subject: 'A memory that makes you smile' },
    { question: 'What did you love most about who they were?', subject: 'What you loved about them' },
    { question: 'How did they shape or impact your life?', subject: 'How they shaped you' },
    { question: 'What message of love would you like to share with their family today?', subject: 'A message for their family' },
  ],

  'Vacation Recap': [
    { question: 'Where did you travel, and who joined you on this trip?', subject: 'The trip and travelers' },
    { question: 'What was the most unforgettable moment of the vacation?', subject: 'The unforgettable moment' },
    { question: 'What surprised you most about this trip?', subject: 'The biggest surprise' },
    { question: 'Share a funny or unexpected story from the journey.', subject: 'A funny moment from the trip' },
    { question: "What's something from this trip you'll always cherish?", subject: "What you'll cherish forever" },
  ],

  'Preserving Memories': [
    { question: 'Whose memories are you preserving, and why does it matter now?', subject: "Whose memories you're keeping" },
    { question: "What's a story about them that should never be forgotten?", subject: 'A story not to be forgotten' },
    { question: 'What made them so special to you and your family?', subject: 'What made them special' },
    { question: 'What tradition or moment do you want future generations to know?', subject: 'A tradition to pass down' },
    { question: "Share a message you'd like loved ones to remember always.", subject: 'A message to remember' },
  ],

  'Birthday Party': [
    { question: 'Whose birthday are you celebrating, and how do you know them?', subject: 'Who they are' },
    { question: "What's your favorite memory with the birthday person?", subject: 'A favorite memory together' },
    { question: 'What do you love or admire most about them?', subject: 'What you admire about them' },
    { question: 'Share a funny story or inside joke between the two of you.', subject: 'An inside joke or funny story' },
    { question: "What's your biggest wish for them in the year ahead?", subject: 'A wish for the year ahead' },
  ],

  'Family Storytelling': [
    { question: 'Which family member or story are you sharing today?', subject: "Who or what you're sharing" },
    { question: "What's a family story passed down that means the most to you?", subject: 'A family story that matters' },
    { question: 'Who in your family shaped you the most, and how?', subject: 'Who shaped you most' },
    { question: 'What tradition do you hope continues for generations?', subject: 'A tradition worth passing on' },
    { question: 'What message would you want the next generation to hear?', subject: 'A message for the next generation' },
  ],

  'Other Milestone': [
    { question: 'What milestone are you celebrating, and who is it for?', subject: "The milestone and who it's for" },
    { question: 'Why is this moment so meaningful right now?', subject: 'Why this moment matters' },
    { question: "What's a memory tied to this milestone you'll never forget?", subject: 'A memory tied to this moment' },
    { question: 'What do you hope they take away from this moment?', subject: 'What you hope they take away' },
    { question: 'Share a message of celebration or encouragement to close it out.', subject: 'A message of celebration' },
  ],
};

// Curated suggestion pool shown when the user clicks the "Add Question"
// modal's text field. ~100 prompts per occasion. Filtered by substring as
// the user types. Selecting one fills the field; the user can also write
// their own.
export const occasionSuggestions = {
  Wedding: [
    'How did you first meet the couple?',
    'Who introduced them, in your view?',
    'Were you there when they started dating?',
    'How did you find out they were getting engaged?',
    'What was your first thought when you met their partner?',
    "What's your relationship to the bride?",
    "What's your relationship to the groom?",
    'How long have you known the couple together?',
    'What role have you played in their relationship?',
    'When did you know they were the real thing?',
    'What was the first sign they were meant to be?',
    'What was your first impression of them as a couple?',
    'Was there a moment they really clicked for you?',
    'What was the first big milestone of theirs you witnessed?',
    'Describe the energy between them when they first started dating.',
    'Tell us about a holiday or vacation memory with the couple.',
    "What's a dinner party or get-together you remember most with them?",
    'Tell us about a road trip you took with them.',
    "What's the most beautiful place you've been with them?",
    "What's a quiet moment with the couple you'll always remember?",
    "What's the most fun you've had with them?",
    'Share a moment you saw them at their best.',
    'Tell us about a time you saw them helping each other.',
    "What's a small everyday memory you cherish about them?",
    'Tell us about a holiday tradition you share with them.',
    'Describe their proposal as you remember it.',
    "What's a moment they made you laugh until your stomach hurt?",
    'Share a moment where their love really showed.',
    'Tell us about something silly the two of them did together.',
    "What's the funniest thing you've witnessed between them?",
    'Share an embarrassing story the bride is OK with.',
    'Share an embarrassing story the groom is OK with.',
    "Tell us about a time things didn't go as planned and you all laughed.",
    "What's an inside joke you share with the couple?",
    'Tell us about a quirky habit the bride has.',
    'Tell us about a quirky habit the groom has.',
    'What only the closest friends know about them?',
    "What's the bride's best quality?",
    "What's the groom's best quality?",
    'How do they bring out the best in each other?',
    'What do you admire about how they communicate?',
    'How do they handle disagreements together?',
    "What's a strength each of them brings to this relationship?",
    'What complements them well as a couple?',
    'How are they different in a way that works?',
    "What are they like when no one's watching?",
    'What surprises you about them as a couple?',
    'How has knowing this couple changed you?',
    "What's something you've learned from watching them?",
    'How has their love influenced your view of relationships?',
    'What does their relationship teach you about love?',
    'What have they taught you about commitment?',
    'How have they been there for you over the years?',
    "What's something they've helped you through?",
    'How are they generous with others?',
    'How do they show up for the people they love?',
    'Tell us about the bachelor or bachelorette party.',
    'Share a memory from their engagement period.',
    'Tell us about meeting their families together.',
    'Tell us about a difficult moment they got through together.',
    'Share a moment that made you sure they would make it forever.',
    "What's the best meal you've shared with them?",
    'Tell us about a trip the three of you took.',
    "What's a perfect memory you have of them together?",
    "What's the best advice you'd give them as they begin married life?",
    "What's a marriage truth you've come to know?",
    "What's something you wish someone had told you on your wedding day?",
    'What advice do you have for keeping love alive?',
    'What does forever look like in your eyes?',
    "What's the secret to a strong partnership?",
    "What's a habit you'd recommend they build together?",
    'How can they keep growing as individuals while growing together?',
    'What should they remember when life gets hard?',
    "What's a piece of marriage wisdom from your own life?",
    "What's your dream for them in their first year of marriage?",
    'What do you hope their home together feels like?',
    'What kind of family do you wish for them?',
    'What adventures do you hope they take together?',
    "What's the legacy you hope they leave?",
    'What do you wish them in tough seasons?',
    'What do you wish them in joyful seasons?',
    'What do you hope they keep doing forever?',
    "What's one thing you hope they always remember?",
    "What's your wish for them on their 50th anniversary?",
    'If they could only hear one thing today, what would you say?',
    "What's your final message to the bride?",
    "What's your final message to the groom?",
    "What's something you've always wanted to tell them?",
    'Leave them with a toast or blessing in your own words.',
    'How would you describe their love in one sentence?',
    'What song reminds you of them?',
    'What movie reminds you of their love story?',
    "What's the best photo of them you can remember?",
    'How do you imagine them as parents one day?',
    'What kind of grandparents do you imagine them being?',
    'How do they balance each other on their hardest days?',
    "What's the smallest gesture you've seen between them that stuck with you?",
    'What did their first home together feel like?',
    'What does it mean to you to be standing here for them today?',
    'How will you remember them on this wedding day?',
    'What promise would you make to them as a friend or family member?',
    "Tell them what you hope they'll never forget about each other.",
    'Send them off with a final word of love.',
  ],

  'New Baby': [
    "What's your relationship to the new parents?",
    "How long have you known the family before this baby's arrival?",
    'What were your feelings the moment you heard about the pregnancy?',
    'Where were you when you found out the baby was on the way?',
    'How did the parents share the news with you?',
    'What did you say when you first heard the baby was coming?',
    'Did you guess the gender before it was revealed?',
    'How did you feel meeting (or seeing) the baby for the first time?',
    "What's the first thing you noticed about the baby?",
    'Who do you think the baby looks like?',
    'What part of the baby do you love most so far?',
    "What's the baby's full name and the story behind it?",
    "What do you hope the baby's name will mean to them?",
    'Share your hope for the baby in one word.',
    'What do you wish for this little one to grow up knowing?',
    'What kind of person do you hope this baby becomes?',
    'What kind of childhood do you hope they have?',
    "What's a value you hope they grow up with?",
    "What's a curiosity you hope they keep their whole life?",
    'What kind of friendships do you hope they have?',
    'What kind of family memories do you hope they make?',
    'What do you hope makes them laugh as they grow?',
    'What do you hope makes them feel safe?',
    'What do you hope is their first big adventure?',
    'What kind of stories do you hope they hear at bedtime?',
    'What kind of music do you hope they grow up loving?',
    'What kind of food do you hope to one day cook for them?',
    'What life skill do you hope they pick up early?',
    'What hobby would you love to teach them?',
    'What sport or activity do you hope they enjoy?',
    'What place do you hope to take them one day?',
    'What book do you hope someone reads to them?',
    'What advice do you have for the new parents?',
    'What advice would you give them about sleep?',
    'What advice would you give them about feeding?',
    'What advice would you give them about routines?',
    'What advice would you give them about staying calm?',
    'What advice would you give them about asking for help?',
    'What advice would you give them about partnership through this?',
    'What surprised you most about being a parent (if you are one)?',
    'What do you think the parents will be best at?',
    "What's a quality from each parent you hope the baby gets?",
    "What's a quality from each parent you hope the baby skips?",
    'How did you see this couple change when they became parents?',
    'How do you see them stepping into this new role?',
    "What's something only you might know about the parents that the baby will love one day?",
    "What's a story about the parents the baby should hear when they're older?",
    "What's a story about your relationship with the parents to share with the baby?",
    'What would you tell the baby about their mom?',
    'What would you tell the baby about their dad?',
    'What would you tell the baby about their grandparents?',
    'What would you tell the baby about their family heritage?',
    'What family traditions do you hope the baby grows up with?',
    'What family stories do you want them to grow up hearing?',
    'What do you want this baby to know about love?',
    'What do you want this baby to know about kindness?',
    'What do you want this baby to know about courage?',
    'What do you want this baby to know about resilience?',
    'What do you want this baby to know about home?',
    'What do you want this baby to know about being themselves?',
    'What do you want this baby to know about asking questions?',
    'What do you want this baby to know about chasing dreams?',
    'What memory of you do you hope the baby holds onto?',
    'What promise would you make this baby today?',
    'What promise would you make to support these parents?',
    'How will you be there for this baby as they grow?',
    'What do you hope to be in this baby\'s life — aunt, uncle, friend, mentor?',
    'What name will the baby call you, and how did it come about?',
    'What special thing do you want to do with this baby someday?',
    'What is the one thing you can\'t wait to teach this baby?',
    'What\'s a milestone of the baby you\'re most excited for?',
    'What\'s a stage of childhood you\'re most excited to see them in?',
    'What do you hope their first word is?',
    'What do you hope their first laugh sounds like?',
    'What hope do you have for their first day of school?',
    'What hope do you have for their teenage years?',
    'What hope do you have for the kind of adult they become?',
    'What\'s a worry you have that you hope dissolves with time?',
    'How did this baby change your view on family?',
    'What gift would you most love to give this baby (intangible)?',
    'What gift would you most love to give this baby (tangible)?',
    'What do you hope this baby teaches the world?',
    'What do you hope the world teaches this baby?',
    'What season was this baby born in, and what does that mean to you?',
    'What\'s a song you imagine singing to them?',
    'What\'s a movie you can\'t wait to watch with them?',
    'What\'s a place you can\'t wait to take them?',
    'What do you want to tell the parents on their hardest night?',
    'What do you want to tell the parents on their best day?',
    'What do you hope their home feels like for this baby?',
    'What do you hope their kitchen smells like growing up?',
    'What do you hope is the baby\'s favorite holiday?',
    'What do you hope is the baby\'s favorite season?',
    'What do you hope is the baby\'s favorite hiding spot?',
    'What do you hope is the baby\'s favorite story?',
    'What do you hope is the baby\'s first favorite toy?',
    'What do you hope is the baby\'s favorite memory of you one day?',
    'What\'s your blessing for this baby today?',
    'What\'s a one-line wish for them as they grow?',
    'Share a final, just-for-this-baby message.',
    'Send the parents one last word of love.',
  ],

  Anniversary: [
    'Whose anniversary is this and what year are they celebrating?',
    'How did you first meet the couple?',
    'How long have you known them as a couple?',
    'What\'s your favorite memory of them from the early years?',
    'What\'s your favorite memory of them from recent years?',
    'What was the first time you saw them really in love?',
    'When did you know they\'d make it for the long haul?',
    'What\'s a small moment between them you\'ve never forgotten?',
    'What\'s the most romantic thing you\'ve seen them do for each other?',
    'What\'s the funniest thing you\'ve seen them do for each other?',
    'What do you admire most about how they communicate?',
    'What do you admire most about how they fight or disagree?',
    'What do you admire most about how they make up?',
    'How do they support each other when things get hard?',
    'How do they celebrate each other\'s wins?',
    'How are they alike in ways that matter?',
    'How are they different in ways that matter?',
    'What does their relationship teach you about partnership?',
    'What does their relationship teach you about patience?',
    'What does their relationship teach you about laughter?',
    'What\'s something only their closest friends know about their love?',
    'What\'s a tradition they have together that you love?',
    'What\'s a habit they\'ve built that\'s stood the test of time?',
    'What was their first home like (if you remember)?',
    'What\'s a vacation memory of them you cherish?',
    'What\'s a holiday memory of them you cherish?',
    'What\'s a milestone you\'ve celebrated with them?',
    'What\'s a hard time you\'ve seen them get through together?',
    'What\'s the kindest thing you\'ve seen them do for each other?',
    'What\'s the most generous thing you\'ve seen from them as a couple?',
    'What\'s a phrase or saying they always use with each other?',
    'What\'s something you wish to thank them for?',
    'How did they help you understand what love can be?',
    'How did they show up for you in your own relationship?',
    'What\'s a piece of advice they\'ve given you?',
    'What\'s a moment they made you feel like family?',
    'How has their love influenced the people around them?',
    'How has their love changed over the years?',
    'What stays the same about them no matter how much time passes?',
    'What\'s your favorite season for them as a couple?',
    'What\'s a tiny detail about them that says everything?',
    'How do they look at each other?',
    'What\'s something you notice when they think no one\'s looking?',
    'Tell us about a time their love made you tear up.',
    'Tell us about a time their love made you laugh.',
    'Share a memory from their wedding (if you were there).',
    'What was the wedding day weather like?',
    'What did their wedding feel like, in three words?',
    'What\'s a wedding-day photo that lives in your head?',
    'What was the most memorable part of their wedding?',
    'What\'s a piece of their love story most people don\'t know?',
    'How have they grown into themselves through this marriage?',
    'How have they grown into each other through this marriage?',
    'What did each of them sacrifice that the other should remember?',
    'What\'s a quality each of them brings to the marriage?',
    'What\'s the biggest gift they\'ve given each other?',
    'What\'s a small daily ritual of theirs you adore?',
    'What\'s a family tradition they\'ve built together?',
    'What kind of parents are/were they (if applicable)?',
    'What kind of friends are they to others?',
    'What kind of hosts are they?',
    'How do they handle long-distance or time apart?',
    'How do they handle stress as a couple?',
    'How do they handle joy as a couple?',
    'What\'s a song that captures their love?',
    'What\'s a movie or book that reminds you of them?',
    'What\'s a meal that feels like them?',
    'What\'s a smell that reminds you of them?',
    'What place captures their relationship best?',
    'What\'s a year you remember being especially good for them?',
    'What\'s a year you remember being hard but worth it for them?',
    'What chapter of their life are you most proud to have witnessed?',
    'What chapter of their life are you most excited for next?',
    'What\'s a wish for them in the year ahead?',
    'What\'s a wish for them in the next decade?',
    'What\'s a wish for them when they\'re old and grey?',
    'How do you hope their love story continues?',
    'What do you hope they keep doing forever?',
    'What do you hope they let go of?',
    'What\'s an adventure you wish for them next?',
    'What\'s a quiet wish you have for them?',
    'What do you hope their next anniversary feels like?',
    'What do you hope their 50th anniversary feels like?',
    'How do you hope their kids/grandkids will describe them?',
    'How do you describe their love to people who haven\'t met them?',
    'What\'s a tribute you\'d give to their love?',
    'What\'s a final word for the partner you know best?',
    'What\'s a final word for the partner you know less?',
    'What\'s your toast to them on this anniversary?',
    'What blessing would you give them today?',
    'What promise do you renew to them as their friend or family?',
    'What\'s a story you\'ve never told them that you should?',
    'What\'s a thank-you you\'ve never given them?',
    'What would you say if today was the only chance to speak to them?',
    'What\'s a message just for the wife/husband?',
    'What\'s a message you want them to play on their next anniversary?',
    'How would you describe them in three words?',
    'What\'s a piece of art that captures their love?',
    'What\'s a photo of them that lives in your heart?',
    'What\'s the love advice you\'d steal from them?',
    'What\'s a habit of theirs you\'ve adopted?',
    'Share a final blessing or wish for the road ahead.',
  ],

  'Celebration of Life': [
    'Who are you honoring today?',
    'What was your relationship to them?',
    'How long did you know them?',
    'How did you first meet?',
    'What was your first impression of them?',
    'What do you remember most clearly about them?',
    'What did they look like in your favorite memory?',
    'What was their voice like?',
    'What was their laugh like?',
    'What was a phrase they used often?',
    'What was a saying that was uniquely theirs?',
    'What did they always wear or carry?',
    'What was their signature meal or dish?',
    'What was their favorite holiday and why?',
    'What was their favorite season?',
    'What was their favorite music?',
    'What was their favorite movie or show?',
    'What was their favorite book?',
    'What did they do for fun?',
    'What was their best skill or craft?',
    'Who did they love most in this world?',
    'How did they show love?',
    'How did they show care without using words?',
    'What did their hugs feel like?',
    'What was a habit of theirs that made you smile?',
    'What was a quirk of theirs you miss?',
    'What was a quirk of theirs you teased them about?',
    'Share a memory of them that always makes you laugh.',
    'Share a memory of them that always makes you cry.',
    'Share a memory of them at a holiday gathering.',
    'Share a memory of them on an ordinary day that felt extraordinary.',
    'Share the last great conversation you had with them.',
    'Share the first big conversation you had with them.',
    'What did they teach you about love?',
    'What did they teach you about loss?',
    'What did they teach you about work?',
    'What did they teach you about kindness?',
    'What did they teach you about courage?',
    'What did they teach you about laughter?',
    'What did they teach you about being yourself?',
    'What did they teach you about family?',
    'What\'s the best advice they ever gave you?',
    'What\'s a piece of wisdom from them you carry every day?',
    'What\'s something they did for you that you\'ve never repaid?',
    'What did they always do for the people they loved?',
    'What did they sacrifice that we should never forget?',
    'What was their proudest moment you witnessed?',
    'What was a moment they were truly themselves?',
    'What was their biggest dream?',
    'What did they overcome that we should remember?',
    'What was a hardship of theirs that built who they were?',
    'What was a quirky talent only their close people knew about?',
    'What was the kindest thing you ever saw them do?',
    'What was the bravest thing you ever saw them do?',
    'What was the funniest thing you ever saw them do?',
    'What was a story they loved to tell?',
    'What was a story they almost never told but should\'ve?',
    'How did they make a room feel?',
    'How did they make you feel about yourself?',
    'How did they show up when life was hard?',
    'How did they celebrate when life was good?',
    'What\'s something they loved about you?',
    'What\'s something you wish you\'d said to them?',
    'What\'s something you DID say to them that you\'re grateful for?',
    'What was your last memory together?',
    'What was your last conversation about?',
    'What was a song that always reminds you of them?',
    'What was a place that always reminds you of them?',
    'What was a smell that always reminds you of them?',
    'What was a meal that always reminds you of them?',
    'What\'s a small everyday thing that brings them back?',
    'What\'s an object of theirs you cherish?',
    'What\'s a photo of them that lives in your mind?',
    'What did they leave behind in you?',
    'What did they leave behind in your family?',
    'What did they leave behind in their community?',
    'What legacy of theirs do you want to carry?',
    'What value of theirs do you want to live by?',
    'What habit of theirs do you want to keep alive?',
    'What part of you came from them?',
    'What part of you was shaped by them?',
    'How will you tell their story to the next generation?',
    'How will you keep their memory alive in your home?',
    'What do you want their grandchildren to know about them?',
    'What do you want strangers to know about who they were?',
    'What\'s a tribute to them in three words?',
    'What\'s your favorite picture of them and why?',
    'What\'s a quote that captures who they were?',
    'What did they always say at the end of a phone call?',
    'What did they always say when leaving?',
    'What did they always say when proud of you?',
    'What did they always say when worried about you?',
    'What is your forever message to them?',
    'What thank-you do you owe them?',
    'What goodbye are you offering today?',
    'What promise do you make in their honor?',
    'What blessing do you send to their family today?',
    'What blessing do you send to yourself in their absence?',
    'How will you honor them today and every day?',
    'What does it mean to celebrate their life?',
    'How do you imagine them now?',
    'What would they want us to remember most?',
    'Share a final word in their memory.',
  ],

  'Vacation Recap': [
    'Where did you travel and when?',
    'Who joined you on the trip?',
    'How long was the trip?',
    'How did this trip come together?',
    'What was the first place you visited?',
    'What was the last place you visited?',
    'What\'s the first thing you noticed when you arrived?',
    'What was the weather like?',
    'What did the place smell like?',
    'What did the food taste like?',
    'What was the best meal of the trip?',
    'What was the worst (or weirdest) meal of the trip?',
    'What was the best place you ate?',
    'What was a hidden-gem restaurant or spot?',
    'What did you eat that you\'d never eaten before?',
    'What\'s a drink that became "the trip drink"?',
    'What was the best view you saw?',
    'What was the most beautiful place you visited?',
    'What was the most unexpected place you visited?',
    'What place would you visit again immediately?',
    'What place would you skip if you went back?',
    'What was the best activity you did?',
    'What activity tested you in a good way?',
    'What activity made you laugh the hardest?',
    'What activity gave you the biggest adrenaline rush?',
    'What sunset (or sunrise) sticks in your mind?',
    'Where did you take the most photos?',
    'What was a moment you wish you had on camera but didn\'t?',
    'What\'s the most photogenic moment of the trip?',
    'What was a conversation you had on the trip you\'ll remember?',
    'What was the longest day of the trip?',
    'What was the slowest, sweetest day of the trip?',
    'What was the most chaotic day of the trip?',
    'What went wrong but became a great story?',
    'What was a near-miss that you laugh about now?',
    'What did you almost forget to pack?',
    'What did you forget to pack?',
    'What did you over-pack?',
    'What did you wish you had brought?',
    'What did you wear too much?',
    'What did you not wear at all?',
    'What\'s a souvenir you brought home and what\'s its story?',
    'What\'s a souvenir you wish you\'d bought?',
    'What\'s something you want to remember from the trip?',
    'What\'s a thing you saw that was unforgettable?',
    'Who did you meet who left an impression?',
    'What\'s a kind gesture from a stranger you\'ll remember?',
    'What\'s a small interaction that made the trip feel real?',
    'What\'s a phrase from the local language you learned?',
    'What\'s a custom or tradition you experienced?',
    'What did you learn about the place you didn\'t know before?',
    'What did you learn about yourself on this trip?',
    'What did you learn about your travel companions?',
    'What did you learn about how you handle change?',
    'What did you learn about how you handle plans falling apart?',
    'What surprised you most about this trip?',
    'What expectation did the trip beat?',
    'What expectation did the trip not meet?',
    'What did you do for the first time on this trip?',
    'What was your bravest moment?',
    'What was your most embarrassing moment?',
    'What was a peaceful moment you didn\'t want to end?',
    'What did you feel on the morning you had to leave?',
    'What\'s a final memory of the trip you keep coming back to?',
    'What music will forever remind you of the trip?',
    'What movie or show captures the trip\'s vibe?',
    'What weather captures the trip\'s vibe?',
    'How did the trip change you?',
    'What\'s a habit you picked up that you\'re keeping?',
    'What\'s a perspective you gained that you\'re keeping?',
    'How did this trip bring you closer to your travel companions?',
    'How did this trip teach you about your travel companions?',
    'What was a "wow" moment of the trip?',
    'What was a "I can\'t believe we did that" moment?',
    'What\'s a story you can\'t tell your parents but loved?',
    'What\'s a story you can\'t WAIT to tell your kids?',
    'What was the highlight in three words?',
    'What was the lowlight in three words?',
    'What was the funniest argument of the trip?',
    'What was the most peaceful evening?',
    'What was the wildest night?',
    'What\'s a person you met you wish you stayed in touch with?',
    'What\'s a place you\'d come back to with someone different?',
    'What\'s the best deal or splurge of the trip?',
    'What\'s the moment you felt most alive?',
    'What\'s the moment you missed home most?',
    'What did you most miss about home?',
    'What did you most appreciate about home when you got back?',
    'What do you want to remember about the trip a year from now?',
    'What do you want to remember about the trip ten years from now?',
    'What do you want to do better on the next trip?',
    'What do you NEVER want to do again on a trip?',
    'What\'s a song from the trip you\'ll add to your forever playlist?',
    'What\'s a tradition you\'d turn into a yearly thing?',
    'How do you hope this trip ages in your memory?',
    'What\'s a single word that captures this vacation?',
    'What\'s a final message to the people you traveled with?',
    'What\'s a trip you want to plan next?',
    'What did this place teach you that you\'ll carry forward?',
    'Share a sentence you want to write in your travel journal.',
    'Share your last image of the trip in your mind.',
    'Send a thank-you to the place itself.',
    'Sign off the trip in your own words.',
  ],

  'Preserving Memories': [
    'Whose memories are you preserving today?',
    'What\'s your relationship to them?',
    'Why does preserving these memories matter to you right now?',
    'When did you decide it was time to capture this?',
    'Who else should hear these memories one day?',
    'What\'s a story about them that should never be lost?',
    'What\'s a story about your family that should never be lost?',
    'What\'s a story about your community that should never be lost?',
    'What\'s a story you heard as a child you want to pass down?',
    'What\'s a family origin story everyone should know?',
    'How did your family come to live where they live?',
    'What was your grandparents\' love story?',
    'What was your great-grandparents\' love story?',
    'How did your parents meet?',
    'What was your childhood home like?',
    'What was a smell that was always in your childhood home?',
    'What was a sound that was always in your childhood home?',
    'What was your favorite room growing up?',
    'What was your bedroom like as a kid?',
    'What were dinners like growing up?',
    'What were the holidays like growing up?',
    'What was a holiday tradition that no longer exists?',
    'What\'s a tradition you want to bring back?',
    'What\'s a tradition you want to keep going?',
    'Who was the heart of every gathering?',
    'Who told the best stories at family events?',
    'Who could quiet a room with a word?',
    'Who made everyone laugh?',
    'Who taught you how to cook?',
    'Who taught you how to fix things?',
    'Who taught you how to handle hard times?',
    'Who taught you about money?',
    'Who taught you about love?',
    'Who taught you about faith or meaning?',
    'What did your family always argue about (lovingly)?',
    'What old photo do you wish came with a story?',
    'What old photo always makes you smile?',
    'What family heirloom carries the most history?',
    'What letter or note from a relative do you treasure?',
    'What recipe carries the most family history?',
    'What food makes you immediately remember someone?',
    'What was your family\'s signature dish?',
    'What was your family\'s "comfort during hard times" food?',
    'What was the family song or hymn?',
    'What was the family motto, said or unsaid?',
    'What\'s a phrase your grandparents always said?',
    'What\'s a phrase your parents always said?',
    'What\'s a phrase your siblings always said?',
    'What\'s your family\'s unique sense of humor?',
    'What was the funniest thing that ever happened at a family event?',
    'What was the most embarrassing thing that ever happened at a family event?',
    'What was the most touching moment at a family event?',
    'What\'s a hard chapter your family lived through?',
    'How did your family get through that hard chapter?',
    'What did your family build from nothing?',
    'What did your family lose that should be remembered?',
    'What did your family gain unexpectedly?',
    'What was a journey or migration in your family\'s story?',
    'What was a war or world event your family lived through?',
    'What was a celebration that became legend in your family?',
    'What was a small family ritual that meant everything?',
    'What was a Sunday like in your family?',
    'What was the day after a holiday like?',
    'What was your family\'s best vacation?',
    'What was the family\'s favorite gathering place?',
    'What was a place that no longer exists you wish you could visit?',
    'Who in your family deserves a chapter and never got one?',
    'Who in your family was misunderstood?',
    'Who in your family quietly held everything together?',
    'Who in your family was the rebel?',
    'Who in your family was the dreamer?',
    'Who in your family was the realist?',
    'What did the eldest always say?',
    'What did the youngest always do?',
    'What sibling dynamic shaped your family?',
    'What pet was loved by everyone?',
    'What stranger became part of your family?',
    'What family friend felt like blood?',
    'What is something you regret not asking your elders?',
    'What is something you wish your kids could ask them now?',
    'What is something your elders said about love?',
    'What is something your elders said about loss?',
    'What is something your elders said about money?',
    'What is something your elders said about home?',
    'What is something your elders said about strangers?',
    'What is something your elders said about God or meaning?',
    'What is a value your family holds tightly?',
    'What is a value your family is letting go of?',
    'What value do you want the next generation to inherit?',
    'What story do you want your great-grandchildren to know?',
    'What family lie do you want to set straight?',
    'What family triumph deserves more credit?',
    'What family wound is still healing?',
    'What family blessing keeps showing up across generations?',
    'What does "family" mean in your house?',
    'How do you want your descendants to remember you?',
    'How do you want your descendants to remember your parents?',
    'How do you want your descendants to remember your grandparents?',
    'What\'s a piece of family wisdom in one sentence?',
    'What\'s a memory you don\'t want time to take?',
    'What\'s a final blessing you offer to all who come after?',
    'Share a memory just for your family\'s eyes.',
    'Share a memory you want shared widely.',
    'Sign off this memory keeper with your hopes.',
  ],

  'Birthday Party': [
    'Whose birthday is it and what year are they marking?',
    'What\'s your relationship to them?',
    'How long have you known them?',
    'How did you two meet?',
    'What was your first impression of them?',
    'When did you know you\'d be friends/family for life?',
    'What\'s your earliest memory of them?',
    'What\'s your most recent memory of them?',
    'What\'s your favorite memory together?',
    'Tell us about a time you laughed until you cried with them.',
    'Tell us about a time they made your day better.',
    'Tell us about a time they were exactly what you needed.',
    'Share an inside joke between the two of you.',
    'Share a nickname they gave you (or you gave them).',
    'Share a phrase they say that always makes you smile.',
    'Share a habit of theirs you find adorable.',
    'Share a habit of theirs that drives you crazy (lovingly).',
    'What\'s their best feature, in your eyes?',
    'What\'s a part of their personality that you love?',
    'What\'s a part of their personality that surprises people?',
    'What\'s a strength they don\'t see in themselves?',
    'What\'s their best skill or talent?',
    'What hobby of theirs do you admire?',
    'What\'s the most generous thing they\'ve done for you?',
    'What\'s the most generous thing they\'ve done for someone else?',
    'When did you see them at their most courageous?',
    'When did you see them at their most vulnerable?',
    'When have they made you proud?',
    'When have they surprised you?',
    'What\'s their go-to drink?',
    'What\'s their go-to meal?',
    'What\'s their guilty pleasure show or movie?',
    'What\'s their guilty pleasure song?',
    'What\'s their dance move?',
    'What\'s their party trick?',
    'What\'s their default outfit?',
    'What\'s a fashion choice of theirs you teased them about?',
    'What\'s a fashion choice of theirs you secretly admire?',
    'What\'s their dream vacation?',
    'What\'s a place you want to take them?',
    'What was their best birthday so far, in your memory?',
    'What was their worst birthday and how did you save it?',
    'What was their funniest birthday moment?',
    'What was a gift they gave you that you still cherish?',
    'What was a gift you gave them they actually liked?',
    'What\'s a tradition you have with them?',
    'What\'s a tradition you want to start with them?',
    'What\'s a place that always reminds you of them?',
    'What\'s a song that always reminds you of them?',
    'What\'s a meal that always reminds you of them?',
    'What\'s a smell that always reminds you of them?',
    'What\'s a piece of advice from them that stuck?',
    'What\'s a piece of advice you have for them?',
    'What did you learn from them this year?',
    'What did you laugh about with them this year?',
    'What did you cry about with them this year?',
    'What did you celebrate with them this year?',
    'What was the highlight of their year?',
    'What was a hard moment of their year you stood beside them in?',
    'What\'s a goal of theirs you saw them work toward?',
    'What\'s a goal of theirs you hope they hit this year?',
    'What\'s the biggest growth you\'ve seen in them recently?',
    'How are they different from a year ago?',
    'How are they exactly the same as you\'ve always known them?',
    'What\'s a side of them more people should see?',
    'What\'s a side of them only you get to see?',
    'What do their friends always say about them?',
    'What do their family members always say about them?',
    'What\'s a story about them you tell everyone?',
    'What\'s a story you\'ve never told them you remember about them?',
    'What\'s something only you and they share?',
    'What\'s something they\'re too humble to take credit for?',
    'What\'s something they should be more proud of?',
    'What\'s something they need to hear today?',
    'What\'s something they need to be told more often?',
    'What\'s a quality of theirs that makes them rare?',
    'What\'s a quality of theirs you want in yourself?',
    'How have they made the world better just by being in it?',
    'What does friendship with them feel like?',
    'What does love from them feel like?',
    'What does silence with them feel like?',
    'What does texting them feel like?',
    'What does showing up at their house feel like?',
    'What\'s your wish for their year ahead?',
    'What\'s your wish for them in five years?',
    'What\'s your wish for their next decade?',
    'What\'s your wish for their love life?',
    'What\'s your wish for their work?',
    'What\'s your wish for their health?',
    'What\'s your wish for their family?',
    'What\'s your wish for their friendships?',
    'What\'s your wish for their dreams?',
    'What\'s your wish for their joy?',
    'What\'s your wish for their rest?',
    'What\'s your wish for their adventures?',
    'What\'s your final birthday message to them?',
    'What promise do you make to them this birthday?',
    'What thank-you do you owe them?',
    'How do you sum them up in three words?',
    'Toast them in your own words.',
    'Sing or hum a line just for them.',
    'Send them off into their next year with one word of love.',
  ],

  'Family Storytelling': [
    'Which family member or story are you telling today?',
    'Why this story, why now?',
    'Who told you this story first?',
    'When did you first hear it?',
    'Who is the central person in the story?',
    'What was their full name and what does it mean?',
    'Where did they live?',
    'Where were they born?',
    'What did they do for work?',
    'What did they do for fun?',
    'What was their love story?',
    'How many siblings did they have?',
    'How did they fit into the family?',
    'What did they look like?',
    'What was their voice like?',
    'What was their laugh like?',
    'What did they wear?',
    'What was their signature dish?',
    'What was their favorite holiday?',
    'What was their favorite season?',
    'What was their favorite music?',
    'What was their favorite book?',
    'What was their favorite story to tell?',
    'What was their favorite phrase?',
    'What was their favorite expression of love?',
    'What did they say at the end of every phone call?',
    'What did they say to comfort you?',
    'What did they say when they were proud of you?',
    'What did they say when they were worried about you?',
    'What did they say about money?',
    'What did they say about marriage?',
    'What did they say about hard work?',
    'What did they say about struggle?',
    'What was their proudest moment?',
    'What was their hardest moment?',
    'What did they overcome that we should remember?',
    'What was a journey or migration in their life?',
    'What war or world event shaped them?',
    'What was their first home like?',
    'What was their last home like?',
    'What was their daily routine?',
    'What was their Sunday look like?',
    'What was their Christmas (or main holiday) look like?',
    'What was a story they always told at gatherings?',
    'What was a story they would NEVER tell?',
    'What was a secret they kept until the end?',
    'What was a regret they shared with you?',
    'What was a triumph they brushed off?',
    'What were they teaching you the last time you saw them?',
    'What was the kindest thing you ever saw them do?',
    'What was the bravest thing you ever saw them do?',
    'What was the funniest thing you ever saw them do?',
    'What did they always do for guests?',
    'What did they always do for kids in the family?',
    'What did they always do at the table?',
    'What did they always do on a long drive?',
    'How did they handle money?',
    'How did they handle conflict?',
    'How did they handle grief?',
    'How did they handle joy?',
    'How did they show love?',
    'How did they show forgiveness?',
    'How did they show discipline?',
    'How did they show belief?',
    'What was their relationship to faith or spirituality?',
    'What was their relationship to nature?',
    'What was their relationship to neighbors?',
    'Who was their closest friend?',
    'Who was their fiercest rival?',
    'Who did they mentor?',
    'Who mentored them?',
    'What was a friendship of theirs that defined them?',
    'What was a feud of theirs that defined them?',
    'What was the smallest gift they gave that meant the most?',
    'What was the biggest gift they gave that meant the most?',
    'What did they leave behind in you?',
    'What part of you came directly from them?',
    'What part of your siblings or cousins came from them?',
    'What part of your kids comes from them?',
    'What lesson of theirs do you live by?',
    'What lesson of theirs are you still trying to learn?',
    'What story of theirs do you want your kids to hear?',
    'What story of theirs do you want their kids to hear?',
    'What family tradition came from them?',
    'What family inside joke came from them?',
    'What family rule came from them?',
    'What family mystery came from them?',
    'What family heirloom did they pass on?',
    'What does our family inherit from them?',
    'How do you keep their memory alive day to day?',
    'How does the family say their name now?',
    'What does saying their name out loud feel like?',
    'What\'s the message from them you want to make sure outlives us all?',
    'What questions about them do you still wish you\'d asked?',
    'What\'s a question we should ask them next time, even in spirit?',
    'What\'s a story you\'ve heard about them you can\'t verify but love?',
    'What\'s a piece of folklore about them that\'s probably true?',
    'What family value should we credit them for?',
    'How does our family carry their legacy?',
    'What\'s your one-line tribute to them?',
    'What\'s your blessing for the next generation in their honor?',
    'How do we make sure their story continues?',
    'Sign off the storytelling with a final word for them.',
  ],

  'Other Milestone': [
    'What milestone are you celebrating?',
    'Who is this milestone for?',
    'How are you connected to the person?',
    'When did this milestone become reality?',
    'How long has this been in the making?',
    'What\'s the story behind this milestone?',
    'What did the journey to this moment look like?',
    'What\'s a part of the journey nobody saw?',
    'What was the hardest part of getting here?',
    'What was the most surprising part of getting here?',
    'Who helped them get here?',
    'Who never stopped believing in them?',
    'Who taught them the most along the way?',
    'Who deserves a thank-you on their behalf today?',
    'What sacrifice did they make to get here?',
    'What sacrifice did their family make to get here?',
    'What\'s the moment you knew this milestone would happen?',
    'What\'s the moment you doubted it would?',
    'What\'s a memory from the early days of this journey?',
    'What\'s a memory from the middle, when it got hard?',
    'What\'s a memory from the recent stretch toward the finish?',
    'How did they handle setbacks?',
    'How did they keep their why front and center?',
    'How did they grow through this?',
    'How are they different now compared to when they started?',
    'What\'s a quality of theirs that made this possible?',
    'What\'s a habit of theirs that got them here?',
    'What\'s a belief they held that carried them?',
    'What\'s a piece of advice that shaped their journey?',
    'What\'s a piece of advice from them that shaped yours?',
    'What\'s a small win along the way that mattered more than people knew?',
    'What\'s a moment of doubt they pushed through?',
    'What\'s a moment of joy they paused to feel?',
    'What\'s a moment of frustration that became a turning point?',
    'What\'s a phrase they kept saying through the journey?',
    'What\'s a song that became their anthem?',
    'What\'s a place that played a big role in this milestone?',
    'What\'s a person who taught them something they couldn\'t have learned alone?',
    'What\'s a book or idea that pushed them forward?',
    'What\'s a tradition you built around this journey?',
    'What\'s a ritual they have for big moments?',
    'How does this milestone fit into their bigger story?',
    'How does this milestone change the road ahead?',
    'What does this milestone mean for the people around them?',
    'What does this milestone mean for their family?',
    'What does this milestone mean for the next generation?',
    'What does this milestone mean for them, deep down?',
    'How do you describe what they\'ve achieved in one sentence?',
    'How do you describe how they did it in one sentence?',
    'What\'s a quote that captures this moment?',
    'What\'s the lesson everyone watching can take away?',
    'What did they teach you while doing this?',
    'What did this milestone teach about persistence?',
    'What did this milestone teach about courage?',
    'What did this milestone teach about identity?',
    'What did this milestone teach about love?',
    'What did this milestone teach about family?',
    'What did this milestone teach about community?',
    'What did this milestone teach about timing?',
    'What did this milestone teach about luck?',
    'What did this milestone teach about hard work?',
    'How do you celebrate them today?',
    'How do you show up for them today?',
    'What does today look like for them, in your eyes?',
    'What gift, big or small, would honor today?',
    'What promise do you make to them about what comes next?',
    'What promise do they need to make to themselves now?',
    'What\'s a worry of yours about the next chapter?',
    'What\'s a hope of yours about the next chapter?',
    'What\'s a wish for them in the next chapter?',
    'What\'s an adventure you hope they take next?',
    'What\'s a rest you hope they take next?',
    'What\'s the next milestone you want to celebrate with them?',
    'What\'s a goal beyond this one for them?',
    'What\'s a piece of wisdom for the road ahead?',
    'What\'s a piece of wisdom they\'ve earned the hard way?',
    'How will this milestone age in their memory?',
    'How will this milestone age in your memory?',
    'How will the people they love remember today?',
    'How will their kids hear about this one day?',
    'What story will they tell strangers about today?',
    'What story will they keep for themselves?',
    'What part of this is unfinished?',
    'What part of this is sealed?',
    'What chapter does this close?',
    'What chapter does this open?',
    'What gratitude is overflowing today?',
    'What feeling is hardest to name today?',
    'What\'s a tear-jerker moment from today?',
    'What\'s a laugh-out-loud moment from today?',
    'What\'s a quiet moment from today?',
    'What\'s an in-jokey moment from today?',
    'What did you tell them face-to-face today?',
    'What did you NOT tell them face-to-face but want to?',
    'What\'s a final blessing for them?',
    'What\'s a tribute in three words?',
    'How do you name today, looking back?',
    'What promise do you make to be there for the next milestone?',
    'What thank-you to give them?',
    'What thank-you do they owe themselves?',
    'How do you toast this moment?',
    'How do you bow out of this celebration with love?',
    'Share a final, just-for-them message.',
  ],
};

export const getQuestionsForOccasion = (occasion) => occasionQuestions[occasion] || occasionQuestions['Other Milestone'];

export const getSuggestionsForOccasion = (occasion) => occasionSuggestions[occasion] || occasionSuggestions['Other Milestone'];

// Cap on the number of base questions shown in the slot strip when multiple
// occasions are selected. Keeps the recorder pace manageable.
const MULTI_OCCASION_QUESTION_CAP = 6;

// Distribute `cap` slots across `n` occasions as evenly as possible.
// Examples (cap=6): [6], [3,3], [2,2,2], [2,2,1,1], [2,1,1,1,1], [1,1,1,1,1,1].
// Leftover slots from short lists do NOT redistribute — keeps the cadence
// predictable even if some occasions have fewer prompts than the share.
const splitCap = (n, cap) => {
  if (n <= 0) return [];
  const base = Math.floor(cap / n);
  const extra = cap % n;
  return Array.from({ length: n }, (_, i) => base + (i < extra ? 1 : 0));
};

// Pick questions across multiple occasions, capped at MULTI_OCCASION_QUESTION_CAP.
// Single occasion → returns its full set unchanged. Empty / null → fallback set.
export const getQuestionsForOccasions = (occasions) => {
  const list = (occasions || []).filter(Boolean);
  if (list.length === 0) return getQuestionsForOccasion(undefined);
  if (list.length === 1) return getQuestionsForOccasion(list[0]);
  const shares = splitCap(list.length, MULTI_OCCASION_QUESTION_CAP);
  const seen = new Set();
  const out = [];
  list.forEach((occ, i) => {
    const pool = getQuestionsForOccasion(occ);
    let taken = 0;
    for (let j = 0; j < pool.length && taken < shares[i]; j += 1) {
      const q = pool[j];
      if (!seen.has(q)) {
        seen.add(q);
        out.push(q);
        taken += 1;
      }
    }
  });
  return out;
};

// Suggestions across multiple occasions: concat all, dedupe, preserve order.
// No cap here — the popup already filters by user input and slices internally.
export const getSuggestionsForOccasions = (occasions) => {
  const list = (occasions || []).filter(Boolean);
  if (list.length === 0) return getSuggestionsForOccasion(undefined);
  if (list.length === 1) return getSuggestionsForOccasion(list[0]);
  const seen = new Set();
  const out = [];
  list.forEach((occ) => {
    getSuggestionsForOccasion(occ).forEach((s) => {
      if (!seen.has(s)) { seen.add(s); out.push(s); }
    });
  });
  return out;
};
